import type {
  CustomViewDataPoint,
  CustomViewSelections,
} from './customViewConfig'
import { useMemo, useState } from 'react'
import {
  ALL_SELECTABLE_DATA_POINTS,
} from './customViewConfig'

export interface UseCustomViewPanelReturn {
  someSelected: boolean
  checked: boolean | 'indeterminate'
  isItemSelected: (key: CustomViewDataPoint) => boolean
  toggleItem: (key: CustomViewDataPoint) => void
  handleSelectAll: () => void
  handleSave: () => void
  handleCancel: () => void
  draftSelectedItems: CustomViewDataPoint[]
}

export function useCustomViewPanel({
  currentSelections,
  onSave,
  onClose,
  selectableItems = ALL_SELECTABLE_DATA_POINTS,
}: {
  currentSelections: CustomViewSelections
  onSave: (selections: CustomViewSelections) => void
  onClose: () => void
  selectableItems?: CustomViewDataPoint[]
}): UseCustomViewPanelReturn {
  const [draft, setDraft] = useState<CustomViewSelections>(currentSelections)

  const selectedCount = selectableItems.filter(k =>
    draft.selectedItems.includes(k),
  ).length
  const allSelected = selectedCount === selectableItems.length
  const someSelected = selectedCount > 0 && !allSelected

  const checked = useMemo(() => {
    if (allSelected)
      return true
    if (someSelected)
      return 'indeterminate'
    return false
  }, [allSelected, someSelected])

  const isItemSelected = (key: CustomViewDataPoint) =>
    draft.selectedItems.includes(key)

  const toggleItem = (key: CustomViewDataPoint) => {
    setDraft(prev => ({
      ...prev,
      selectedItems: prev.selectedItems.includes(key)
        ? prev.selectedItems.filter(k => k !== key)
        : [...prev.selectedItems, key],
    }))
  }

  const handleSelectAll = () => {
    if (allSelected || someSelected) {
      setDraft(prev => ({ ...prev, selectedItems: [] }))
    }
    else {
      setDraft(prev => ({
        ...prev,
        selectedItems: [...selectableItems],
      }))
    }
  }

  const handleSave = () => {
    onSave(draft)
    onClose()
  }

  const handleCancel = () => {
    setDraft(currentSelections)
    onClose()
  }

  return {
    checked,
    someSelected,
    draftSelectedItems: draft.selectedItems,
    isItemSelected,
    toggleItem,
    handleSelectAll,
    handleSave,
    handleCancel,
  }
}
